import axios from 'axios';

const BASE_URL = 'https://api.escuela.agentesia.cloud/api/v1';

// Crear instancia de axios
const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('student_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // NO hacer logout automáticamente - dejar que los componentes manejen los errores
    // El logout automático causa mala experiencia de usuario
    if (error.response?.status === 401) {
      console.warn('⚠️ Error 401 recibido pero no se hace logout automáticamente:', error.response?.data?.message);
      // Comentamos el logout automático para evitar que saque a los usuarios del QUIZ
      // localStorage.removeItem('student_token');
      // if (!window.location.pathname.includes('login') && window.location.pathname !== '/') {
      //   window.location.reload();
      // }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post('/students/auth/login', { email, password }),
    
  loginWith2FA: (data: { tempToken: string; twoFactorCode: string }) =>
    apiClient.post('/students/auth/login/2fa', data),
    
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => apiClient.post('/students/auth/register', data),
  
  verifyToken: () => apiClient.get('/students/auth/me'),
};

// Courses API
export const coursesApi = {
  getAll: () => apiClient.get('/courses'),
  
  getById: (id: string) => apiClient.get(`/students/courses/${id}`),
  
  getEnrolled: () => apiClient.get('/students/courses'),
  
  enroll: (courseId: string, paymentData?: any) =>
    apiClient.post(`/courses/${courseId}/enroll`, { paymentData }),
    
  getProgress: (courseId: string) =>
    apiClient.get(`/courses/${courseId}/progress`),
    
  updateProgress: (courseId: string, progress: {
    moduleId: string;
    completed: boolean;
    timeSpent: number;
  }) => apiClient.post(`/courses/${courseId}/progress`, progress),
};

// Payments API
export const paymentsApi = {
  createWompiCoursePayment: (courseId: string, customerData?: any) =>
    apiClient.post(`/payments/course/${courseId}/wompi`, customerData),
    
  verifyWompiPayment: (transactionId: string, courseId?: string) =>
    apiClient.get(`/payments/wompi/verify/${transactionId}`, { params: { courseId } }),
    
  getPaymentHistory: () => apiClient.get('/payments/history'),
};

// Students API
export const studentsApi = {
  getProfile: () => apiClient.get('/students/auth/me'),
  
  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    avatar?: string;
  }) => apiClient.put('/students/profile', data),
  
  uploadAvatar: (formData: FormData) =>
    apiClient.post('/students/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
    
  changePassword: (data: {
    currentPassword: string;
    newPassword: string;
  }) => apiClient.post('/students/auth/change-password', data),

  enable2FA: () => apiClient.post('/students/auth/enable-2fa'),
  
  verify2FA: (data: { token: string }) => 
    apiClient.post('/students/auth/verify-2fa', data),
  
  disable2FA: (data: { password: string; token?: string }) => 
    apiClient.post('/students/auth/disable-2fa', data),
  
  getEnrolledCourses: () => apiClient.get('/students/courses'),
  
  getCertificates: () => apiClient.get('/students/certificates'),
  
  downloadCertificate: (courseId: string) =>
    apiClient.get(`/students/certificates/${courseId}/download`, {
      responseType: 'blob',
    }),
};

// Videos API
export const videosApi = {
  getVideoUrl: (lessonId: string) =>
    apiClient.get(`/videos/lesson/${lessonId}/url`),
    
  updateProgress: (lessonId: string, progress: {
    watchTime: number;
    completed?: boolean;
  }) => apiClient.post(`/videos/lesson/${lessonId}/progress`, progress),
};

// Notifications API
export const notificationsApi = {
  getAll: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    apiClient.get('/students/notifications', { params }),

  getUnreadCount: () => apiClient.get('/students/notifications/unread-count'),

  markAsRead: (notificationIds: string[]) =>
    apiClient.patch('/students/notifications/mark-read', { notificationIds }),
};

// Exercises API
export const exercisesApi = {
  getLessonQuestions: (lessonId: string) =>
    apiClient.get(`/exercises/lesson/${lessonId}/questions`),

  submitQuiz: (lessonId: string, answers: { [questionId: string]: string }) =>
    apiClient.post(`/exercises/${lessonId}/submit`, { answers }),

  submitExercise: (exerciseId: string, answers: { [questionId: string]: string }) =>
    apiClient.post(`/exercises/${exerciseId}/submit`, { answers }),
};

export default apiClient;